/**
 * DTOs para Fase 1 do refactor de emissão de NF Empresa.
 *
 * Contrato-alvo (Genie card [m1O7KeaU]):
 * - GET /api/v1/... /pendentes-faturamento — lista NF Colab com saldo > 0
 * - GET /api/v1/companies/{id}/notas-fiscais/notas-mae — combo Nota-Mãe
 *
 * Regras do webpanel `EmpresaGerarNotaFiscalColaborador`:
 * - Filtro: `status <> NSO AND status <> CON AND (valorReceberFormula - valorRecebido) > 0`
 * - Nota-Mãe: `EmpresaCodigo = X AND NotaMae AND status = EMI AND NotaMaeSaldo > 0`
 * - Modelo fixo: `ModeloNotaFiscal.RPSNFSe` (populado no start; readonly)
 */

/** Status válidos no grid de pendências (todos exceto NSO/CON). */
export type NotaFiscalColabPendenteStatus = 'SOL' | 'APR' | 'REP' | 'PAP' | 'PEM' | 'EMI';

/** 1 linha do grid de pendências (mapeamento UI ↔ KB documentado no card). */
export interface NotaFiscalColabPendenteItem {
  /**
   * PK composta `"{colaboradorCodigo}-{colaboradorNotaFiscalCodigo}"` (ex.: `"8-37"`).
   *
   * Era só `ColaboradorNotaFiscalCodigo` até ATD-0036 (card [07WbeU4u]), o que
   * colidia entre colaboradores. Usar `parseColabNotaFiscalCodigo()` para
   * extrair o código — `Number(id)` agora devolve `NaN`.
   */
  id: string;
  colaboradorCodigo: number;
  colaboradorNome: string;
  clienteCodigo: number;
  clienteNome: string;
  mes: number;
  ano: number;
  /** "JANEIRO/2026" — legível pra UI. */
  periodo: string;
  status: NotaFiscalColabPendenteStatus;
  /** Coluna UI "Horas" = `EspelhoTotalHoras`. */
  espelhoTotalHoras: number;
  /** Bruto do espelho — não exibido no grid mas exposto pra Fases 2/3. */
  espelhoValor: number;
  /** Coluna UI "Horas (Apontadas)" = `ApontamentoTotalHorasApontadas`. */
  apontamentoTotalHorasApontadas: number;
  /** Coluna UI "Valor [Pagar]" = `ApontamentoValor`. */
  apontamentoValor: number;
  /** Coluna UI "Valor [Receber]" = `ValorReceberFormula`. */
  valorReceberFormula: number;
  /** Coluna UI "Valor [Recebido]" = `ValorRecebido`. */
  valorRecebido: number;
  /** `ValorReceberFormula - ValorRecebido` (calculado no backend). */
  saldoPendente: number;
}

/**
 * Extrai `ColaboradorNotaFiscalCodigo` do `id` composto `"colab-nf"`.
 *
 * Tolera o formato antigo (`id` só com o código) para não quebrar caso o
 * upstream volte atrás: sem hífen, o valor inteiro é o código.
 */
export function parseColabNotaFiscalCodigo(id: string): number {
  const partes = id.split('-');
  return Number(partes[partes.length - 1]);
}

/**
 * 1 linha do espelho do WC `EmpresaNotaFiscalColaboradorAutomatica` (Fase 2 —
 * card [kL1eeQPT]). Backend decide `emissaoOK` conforme cobertura da RC:
 * `true` = RC cobre → NF será emitida; `false` = residual/sem RC → só informa.
 */
export interface PreviewAutomaticaLinha {
  clienteCodigo: number;
  clienteNome: string;
  clienteProjetoCodigo: number;
  clienteProjetoNome: string;
  /** `null` em linha residual (`emissaoOK=false`). */
  requisicaoCompraCodigo: number | null;
  /** `null` em linha residual. */
  requisicaoCompraReferencia: string | null;
  valor: number;
  emissaoOK: boolean;
}

/** Body do preview automática (Fase 2). Empresa vem no path. */
export interface PreviewAutomaticaInput {
  modelo: string;
  serie: string;
  colaboradorCodigo: number;
  colaboradorNotaFiscalCodigo: number;
  notaMaeEmpresaCodigo: number | null;
  notaMaeEmpresaNotaFiscalCodigo: number | null;
}

/**
 * 1 linha do grid editável do WC `EmpresaNotaFiscalColaboradorManual` (Fase 3 —
 * card [WmXNN13e]). Backend retorna RCs disponíveis; front adiciona valor + OK.
 */
export interface ManualRequisicaoCompra {
  requisicaoCompraCodigo: number;
  requisicaoCompraReferencia: string;
  requisicaoCompraSaldo: number;
  /** String livre (ex.: "[SRTA. MARIA LUIZA SAMPAIO]"). */
  requisicaoCompraProjetosVinculados: string;
}

/** 1 opção do combo Nota-Mãe. */
export interface NotaMaeOption {
  /** `EmpresaNotaFiscalCodigo` (int64 como string). */
  id: string;
  /** `EmpresaNotaFiscalNotaMaeTitulo` — description do combo no WWP. */
  titulo: string;
  numero: number | null;
  /** `EmpresaNotaFiscalNotaMaeSaldo` — exibido ao lado do combo. */
  saldo: number;
  valor: number;
  dataHora: string;
}
